import React, { useState } from "react";
import {
  Group,
  Anchor,
  FileInput,
  Input,
  Text,
  List,
  ThemeIcon,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
  IconDownload,
  IconCircleCheck,
  IconExclamationMark,
} from "@tabler/icons";
import { useServerApi } from "../hooks/userServerApi";

const ApprovalFundListFileImporter = ({
  form,
  label,
  required,
  withAsterisk,
  error: fieldError,
}) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [parsedCount, setParsedCount] = useState(0);
  const [api] = useServerApi();

  const handleDownloadTemplate = async () => {
    setDownloadLoading(true);
    try {
      const result = await api.ApprovalFundList.downloadTemplate();
      if (!result?.success) {
        throw new Error(result?.error || "Failed to download template");
      }
    } catch (err) {
      showNotification({
        title: "Download failed",
        message: err.message,
        color: "red",
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleFileUpload = async (selectedFile) => {
    setFile(selectedFile);
    if (!selectedFile) {
      setImportError(null);
      setParsedCount(0);
      form.setFieldValue("funds", []);
      form.setFieldValue("fileName", null);
      return;
    }

    setLoading(true);
    setImportError(null);

    try {
      const result = await api.ApprovalFundList.parseFundFile(selectedFile);
      if (!result?.success) {
        throw new Error(
          result?.error?.msg || result?.message || "Import failed"
        );
      }

      const funds = result.data?.funds ?? [];
      form.setFieldValue("funds", funds);
      form.setFieldValue("fileName", selectedFile.name);
      if (funds.length > 0) {
        form.clearFieldError("funds");
      }
      setParsedCount(funds.length);

      showNotification({
        title: `${selectedFile.name} parsed successfully`,
        message: `${funds.length} fund(s) loaded`,
        color: "teal",
      });
    } catch (err) {
      setImportError(err.message);
      setParsedCount(0);
      form.setFieldValue("funds", []);
      form.setFieldValue("fileName", null);
      form.setFieldError(
        "funds",
        "Please upload a fund list file with at least one fund"
      );
      showNotification({
        title: "Import failed",
        message: err.message,
        color: "red",
        icon: <IconExclamationMark size={18} />,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Input.Wrapper
      label={label ?? "Fund List File"}
      required={required}
      withAsterisk={withAsterisk ?? required}
      error={fieldError || form.errors?.funds}
    >
      <FileInput
        placeholder="Pick file"
        value={file}
        onChange={handleFileUpload}
        accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx,.xls"
        disabled={loading}
      />

      <Group mt="md" spacing="xs">
        <IconDownload size="1rem" />
        <Anchor
          size="xs"
          component="button"
          type="button"
          onClick={handleDownloadTemplate}
          disabled={downloadLoading}
          sx={{ opacity: downloadLoading ? 0.5 : 1 }}
        >
          {downloadLoading ? "Downloading..." : "Download Template"}
        </Anchor>
      </Group>

      {!loading && file && !importError && (
        <List
          mt="md"
          spacing="xs"
          size="sm"
          icon={
            <ThemeIcon color="teal" size={24} radius="xl">
              <IconCircleCheck size={16} />
            </ThemeIcon>
          }
        >
          <List.Item>File format is valid. [OK]</List.Item>
          <List.Item>{parsedCount} fund(s) parsed from file. [OK]</List.Item>
        </List>
      )}

      {importError && (
        <Text size="sm" color="red" mt="xs">
          {importError}
        </Text>
      )}
    </Input.Wrapper>
  );
};

export default ApprovalFundListFileImporter;
